/**
 * Market Intelligence Service
 * Fetches real-time regulatory and market data
 */

// Only hit the network when a real API origin is configured. Without it, the old
// hardcoded default (https://api.bootminds.com) was unresolvable and logged a
// console error on every Market Intelligence visit. When unset, methods return
// their static fallbacks directly — identical UX, no failed request.
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
const API_CONFIGURED = Boolean(API_BASE_URL);
const CACHE_DURATION = 3600000; // 1 hour in milliseconds

class MarketIntelligenceService {
  constructor() {
    this.cache = new Map();
  }

  /**
   * Get cached data or fetch fresh data
   */
  async getCachedData(key, fetchFunction) {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }

    const data = await fetchFunction();
    this.cache.set(key, { data, timestamp: Date.now() });
    return data;
  }

  /**
   * Fetch regulatory updates from API or KV storage
   */
  async getRegulatoryUpdates() {
    if (!API_CONFIGURED) return this.getFallbackRegulatoryData();
    try {
      const response = await fetch(`${API_BASE_URL}/api/regulatory-updates`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch regulatory updates');
      }

      const data = await response.json();
      return data.updates || this.getFallbackRegulatoryData();
    } catch (error) {
      console.error('Error fetching regulatory updates:', error);
      return this.getFallbackRegulatoryData();
    }
  }

  /**
   * Fetch compliance scores by state
   */
  async getComplianceScores() {
    if (!API_CONFIGURED) return this.getFallbackComplianceData();
    try {
      const response = await fetch(`${API_BASE_URL}/api/compliance-scores`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch compliance scores');
      }

      const data = await response.json();
      return data.scores || this.getFallbackComplianceData();
    } catch (error) {
      console.error('Error fetching compliance scores:', error);
      return this.getFallbackComplianceData();
    }
  }

  /**
   * Fetch upcoming regulatory changes
   */
  async getUpcomingChanges() {
    if (!API_CONFIGURED) return this.getFallbackUpcomingChanges();
    try {
      const response = await fetch(`${API_BASE_URL}/api/upcoming-changes`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch upcoming changes');
      }

      const data = await response.json();
      return data.changes || this.getFallbackUpcomingChanges();
    } catch (error) {
      console.error('Error fetching upcoming changes:', error);
      return this.getFallbackUpcomingChanges();
    }
  }

  /**
   * Fetch all market intelligence data
   */
  async getAllMarketData() {
    return this.getCachedData('all-market-data', async () => {
      const [regulatory, compliance, upcoming] = await Promise.all([
        this.getRegulatoryUpdates(),
        this.getComplianceScores(),
        this.getUpcomingChanges(),
      ]);

      return {
        regulatory,
        compliance,
        upcoming,
        lastUpdated: new Date().toISOString(),
      };
    });
  }

  /**
   * Fallback data for regulatory updates
   */
  getFallbackRegulatoryData() {
    // Returns a static, factual regulatory landscape snapshot rather than fabricated dated updates.
    // For up-to-date specifics see /resources/india-gcc-regulatory-guide.
    return [
      {
        title: 'Companies Act, 2013',
        description: 'Foundational statute for India entity formation, ROC filings, board governance, and director responsibilities. Administered by MCA.',
        status: 'active',
        date: 'In force',
        impact: 'High'
      },
      {
        title: 'FEMA, 1999 + RBI Master Directions',
        description: 'Foreign Direct Investment, External Commercial Borrowings, Liberalised Remittance Scheme, and dividend repatriation rules. Administered by RBI.',
        status: 'active',
        date: 'In force',
        impact: 'High'
      },
      {
        title: 'Income Tax Act / Transfer Pricing (§ 92 series)',
        description: 'Transfer pricing documentation, APA programme, and arm\'s-length compliance for related-party transactions between GCC and parent.',
        status: 'active',
        date: 'In force',
        impact: 'High'
      },
      {
        title: 'CGST/SGST/IGST Acts',
        description: 'GST registration thresholds, monthly returns (GSTR-1, 3B), and input tax credit rules for India GCC operations.',
        status: 'active',
        date: 'In force',
        impact: 'Medium'
      },
      {
        title: 'Digital Personal Data Protection (DPDP) Act, 2023',
        description: 'Data fiduciary obligations for any GCC processing personal data of Indian principals. Phased operationalisation underway.',
        status: 'active',
        date: 'In force',
        impact: 'High'
      },
      {
        title: 'Indian Labour Codes',
        description: 'Four consolidated codes (Wages, Industrial Relations, Social Security, OSH&WC) with phased state-by-state rollout. EPF/ESI/Gratuity remain mandatory.',
        status: 'pending',
        date: 'Phased rollout',
        impact: 'Medium'
      }
    ];
  }

  /**
   * Fallback data for compliance scores
   */
  getFallbackComplianceData() {
    return [
      { state: "Karnataka", score: 9.2, change: "+0.3" },
      { state: "Telangana", score: 8.8, change: "+0.1" },
      { state: "Tamil Nadu", score: 8.5, change: "0.0" },
      { state: "Maharashtra", score: 8.3, change: "-0.2" },
      { state: "Haryana", score: 8.1, change: "+0.4" }
    ];
  }

  /**
   * Fallback data for upcoming changes
   */
  getFallbackUpcomingChanges() {
    return [
      {
        title: "Data Protection Act 2025",
        date: "Aug 15, 2025",
        impact: "High"
      },
      {
        title: "Labor Code Amendment",
        date: "Sep 1, 2025",
        impact: "Medium"
      },
      {
        title: "Tax Policy Update",
        date: "Oct 1, 2025",
        impact: "High"
      },
      {
        title: "Environmental Compliance",
        date: "Nov 15, 2025",
        impact: "Low"
      }
    ];
  }

  /**
   * Clear cache
   */
  clearCache() {
    this.cache.clear();
  }
}

// Export singleton instance
export default new MarketIntelligenceService();
